import { ManIcon, WomanIcon } from '@eg/elements/components/Icons';
import { ChangeEvent, FC, ReactElement, useState } from 'react';
import { Gender, PhysicalAspectFormRadioTitle, TrackingGender } from 'rlv-common';
import { FieldNumberInput } from '../../../../components/FormComponents';
import FormTemplate from '../../../../components/FormComponents/FormTemplate/FormTemplate';
import InfoText from '../../../../components/InfoText/infoText';
import RadioGroup from '../../../../components/RadioGroup/RadioGroup';
import { RadioGroupOption } from '../../../../components/RadioGroup/RadioGroup.types';
import { Storage } from '../../../../helpers/Storage';
import { changeFieldTouched, isFormFieldTouched } from '../../../../helpers/form';
import { getGender } from '../../../../helpers/genderHelper';
import { usePreviousAndTrackError } from '../../../../hooks/usePreviousAndTrackError/usePreviousAndTrackError';
import { TrackingElementGender } from '../../../../tracking/tracking.types';
import { withTracker } from '../../../../tracking/withTracker';
import { AemFragmentCatalog } from '../../../../types/AemFragmentCatalog';
import { TemplateLayout } from '../../../../types/FormTemplate.types';
import { SessionStorageKeys } from '../../../../types/SessionStorage';
import { createLivingConditionsPhysicalAspectSchema } from '../../../../validation/LivingConditionsPage';
import {
  PhysicalAspectFormNames,
  PhysicalAspectFormProps,
  PhysicalAspectFormText,
  PhysicalAspectFormValues,
} from './PhysicalAspect.types';
import './PhysicalAspectForm.css';

const physicalAspectFormTemplateLayout: TemplateLayout<object> = {
  title: PhysicalAspectFormText.TITLE,
  virtualAssistantFragment: AemFragmentCatalog.STEP8VA,
  footerInfoSection: () => <InfoText textBeforeLink={PhysicalAspectFormText.FOOTER} />,
};

const RADIO_GROUP_OPTIONS: RadioGroupOption[] = [
  {
    title: PhysicalAspectFormRadioTitle.MAN,
    value: Gender.MALE,
    icon: <ManIcon className="physical-aspect-form__icon" />,
  },
  {
    title: PhysicalAspectFormRadioTitle.WOMAN,
    value: Gender.FEMALE,
    icon: <WomanIcon className="physical-aspect-form__icon" />,
  },
];

const PhysicalAspectForm: FC<PhysicalAspectFormProps> = ({
  height,
  weight,
  disabled,
  onSubmit,
  onNavigateNoSubmit,
  tracker,
}): ReactElement => {
  const currentStoredGender = isFormFieldTouched('gender') ? getGender() : undefined;
  const [hiddenInputs, setHiddenInputs] = useState<boolean>(!currentStoredGender);
  const { compareErrors } = usePreviousAndTrackError({ height: '', weight: '' });

  return (
    <div className="container">
      <FormTemplate<PhysicalAspectFormValues>
        templateLayout={physicalAspectFormTemplateLayout}
        formValues={{ gender: currentStoredGender, height, weight }}
        validationSchema={createLivingConditionsPhysicalAspectSchema()}
        handleNavigateNoSubmitAction={onNavigateNoSubmit}
        handleOnSubmitAction={onSubmit}
        render={[
          {
            name: PhysicalAspectFormNames.GENDER,
            component: (form, field) => (
              <div className="physical-aspect-form__radio-buttons">
                <RadioGroup
                  options={RADIO_GROUP_OPTIONS}
                  name={PhysicalAspectFormNames.GENDER}
                  selectedValue={field.value}
                  disabled={disabled}
                  onChangeSelection={(_, gender) => {
                    Storage.writeItem(SessionStorageKeys.GENDER, gender as string);
                    if (hiddenInputs) {
                      setHiddenInputs(false);
                    }
                    form.setFieldValue(field.name, gender);
                    changeFieldTouched('gender');

                    const genderOption = RADIO_GROUP_OPTIONS.find(option => option.value === gender);
                    if (genderOption && genderOption.title === PhysicalAspectFormRadioTitle.MAN) {
                      tracker.updateUserAttributes({ gender: TrackingGender.MALE });
                      tracker.trackClickEvent({
                        clickedElement: TrackingElementGender.RADIOBUTTOM_MALE,
                      });
                    } else if (
                      genderOption &&
                      genderOption.title === PhysicalAspectFormRadioTitle.WOMAN
                    ) {
                      tracker.updateUserAttributes({ gender: TrackingGender.FEMALE });
                      tracker.trackClickEvent({
                        clickedElement: TrackingElementGender.RADIOBUTTION_FEMALE,
                      });
                    }
                  }}
                />
              </div>
            ),
          },
          {
            name: PhysicalAspectFormNames.HEIGHT,
            shouldBeHidden: hiddenInputs,
            component: (form, field) => (
              <div className="physical-aspect-form__input-wrapper">
                <div className="physical-aspect-form__input-container">
                  <FieldNumberInput
                    name={field.name}
                    label="Größe"
                    placeholder="z.B. 180"
                    adornmentRight="cm"
                    onBlur={() => compareErrors('height', form.errors[field.name])}
                    onChange={({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
                      tracker.updateAdditionalTariffOptions({ KGroesse: value });
                      form.setFieldValue(field.name, Number(value));
                      changeFieldTouched('height');
                    }}
                  />
                </div>
                {form.errors[field.name] && form.touched[field.name] && (
                  <p className="error-message">{form.errors[field.name]}</p>
                )}
              </div>
            ),
          },
          {
            name: PhysicalAspectFormNames.WEIGHT,
            shouldBeHidden: hiddenInputs,
            component: (form, field) => (
              <div className="physical-aspect-form__input-wrapper">
                <div className="physical-aspect-form__input-container">
                  <FieldNumberInput
                    name={field.name}
                    label="Gewicht"
                    placeholder="z.B. 80"
                    adornmentRight="kg"
                    onBlur={() => compareErrors('weight', form.errors[field.name])}
                    onChange={({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
                      tracker.updateAdditionalTariffOptions({ KGewicht: value });
                      form.setFieldValue(field.name, Number(value));
                      changeFieldTouched('weight');
                    }}
                  />
                </div>
                {form.errors[field.name] && form.touched[field.name] && (
                  <p className="error-message">{form.errors[field.name]}</p>
                )}
              </div>
            ),
          },
        ]}
      />
    </div>
  );
};

export default withTracker(PhysicalAspectForm);
